/**
 * Page /grc/infos-pratiques — back-office « Infos pratiques » (GRC, migration 116).
 *
 * Alimente le Pôle Info visible par les administrés (page /mes-alertes enrichie).
 * Deux sections gérées :
 *   · Horaires des services (nom, horaires libre, adresse, tél, email, note ;
 *     affiché/masqué ; réordonnable).
 *   · Aide aux démarches (titre, texte, pièces, PDF optionnel ; affiché/masqué ;
 *     réordonnable).
 *
 * Conforme au guide des briques : GrcLayout (AppHeader + GrcNav), zéro
 * window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, SectionEyebrow, Input, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { AgendaManager } from '@/components/grc/AgendaManager';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { ServiceHoraire, Demarche, LienDemarcheManuel } from '@/lib/api';
import {
  Clock, FileText, Plus, Pencil, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
  Paperclip, AlertCircle, X, CalendarDays,
} from 'lucide-react';

export default function InfosPratiquesPage() {
  const { confirm, dialogElement } = useConfirmDialog();

  const [services, setServices] = useState<ServiceHoraire[]>([]);
  const [demarches, setDemarches] = useState<Demarche[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modales
  const [svcEdit, setSvcEdit] = useState<ServiceHoraire | 'new' | null>(null);
  const [demEdit, setDemEdit] = useState<Demarche | 'new' | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, d] = await Promise.all([
        api.grc.poleInfo.listServices(),
        api.grc.poleInfo.listDemarches(),
      ]);
      setServices(s.services);
      setDemarches(d.demarches);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  // ── Actions services ───────────────────────────────────────────────────────
  async function toggleService(s: ServiceHoraire) {
    try { await api.grc.poleInfo.updateService(s.id, { ...s, affiche: !s.affiche }); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function deleteService(s: ServiceHoraire) {
    const ok = await confirm({ title: 'Supprimer ce service ?', description: `« ${s.nom} » sera retiré du pôle info.`, danger: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    try { await api.grc.poleInfo.deleteService(s.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function moveService(idx: number, dir: -1 | 1) {
    const next = [...services];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setServices(next);
    try { await api.grc.poleInfo.reordreServices(next.map((x) => x.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); void reload(); }
  }

  // ── Actions démarches ──────────────────────────────────────────────────────
  async function toggleDemarche(d: Demarche) {
    try { await api.grc.poleInfo.updateDemarche(d.id, { titre: d.titre, texte: d.texte, pieces: d.pieces, affiche: !d.affiche }); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function deleteDemarche(d: Demarche) {
    const ok = await confirm({ title: 'Supprimer cette démarche ?', description: `« ${d.titre} » et son éventuel PDF seront retirés.`, danger: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    try { await api.grc.poleInfo.deleteDemarche(d.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function moveDemarche(idx: number, dir: -1 | 1) {
    const next = [...demarches];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setDemarches(next);
    try { await api.grc.poleInfo.reordreDemarches(next.map((x) => x.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); void reload(); }
  }

  return (
    <GrcLayout active="infos">
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 animate-fade-in">
        <SectionEyebrow as="div">Communication municipale</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
          Infos pratiques affichées aux administrés
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          Ces informations alimentent le pôle info visible par tous les habitants. Les menus de cantine et
          les événements d'associations y sont agrégés automatiquement — rien à recopier ici.
        </p>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>
        )}

        {/* ===== Agenda municipal (migration 129) ===== */}
        <div className="mt-6">
          <SectionRepliable
            title="Agenda municipal"
            icon={<CalendarDays className="h-5 w-5 text-grc" aria-hidden="true" />}
            defaultOpen
          >
            <AgendaManager />
          </SectionRepliable>
        </div>

        {/* ===== Horaires des services ===== */}
        <div className="mt-6">
          <SectionRepliable
            title="Horaires des services"
            icon={<Clock className="h-5 w-5 text-grc" aria-hidden="true" />}
            right={
              <Button variant="primary" size="sm" onClick={() => setSvcEdit('new')}>
                <Plus className="h-4 w-4" /> Ajouter
              </Button>
            }
          >
            {loading ? (
              <p className="text-sm text-ink-muted">Chargement…</p>
            ) : services.length === 0 ? (
              <p className="text-sm text-ink-muted">Aucun service. Ajoutez-en un pour l'afficher aux habitants.</p>
            ) : (
              <ul className="divide-y divide-border">
                {services.map((s, idx) => (
                  <li key={s.id} className="flex items-start gap-3 py-3">
                    <div className="flex flex-col gap-0.5 pt-0.5">
                      <button type="button" aria-label="Monter" disabled={idx === 0}
                        onClick={() => moveService(idx, -1)}
                        className="text-ink-dim hover:text-ink disabled:opacity-30">
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button type="button" aria-label="Descendre" disabled={idx === services.length - 1}
                        onClick={() => moveService(idx, 1)}
                        className="text-ink-dim hover:text-ink disabled:opacity-30">
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink">{s.nom}</span>
                        {!s.affiche && <Badge variant="neutral" size="sm">Masqué</Badge>}
                      </div>
                      <div className="mt-0.5 text-sm text-ink-muted">
                        {[s.horaires, s.adresse, s.telephone, s.email].filter(Boolean).join(' · ') || '—'}
                      </div>
                      {s.note && <div className="mt-0.5 text-xs text-ink-dim">Note : {s.note}</div>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" aria-label={s.affiche ? 'Masquer' : 'Afficher'}
                        onClick={() => toggleService(s)}
                        className="rounded-r-md p-1.5 text-ink-muted hover:bg-grc-tint/40 hover:text-grc-fg">
                        {s.affiche ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button type="button" aria-label="Modifier" onClick={() => setSvcEdit(s)}
                        className="rounded-r-md p-1.5 text-ink-muted hover:bg-grc-tint/40 hover:text-grc-fg">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" aria-label="Supprimer" onClick={() => deleteService(s)}
                        className="rounded-r-md p-1.5 text-ink-muted hover:bg-urgent-tint/40 hover:text-urgent">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionRepliable>
        </div>

        {/* ===== Aide aux démarches ===== */}
        <div className="mt-2">
          <SectionRepliable
            title="Aide aux démarches"
            icon={<FileText className="h-5 w-5 text-grc" aria-hidden="true" />}
            right={
              <Button variant="primary" size="sm" onClick={() => setDemEdit('new')}>
                <Plus className="h-4 w-4" /> Ajouter
              </Button>
            }
          >
            {loading ? (
              <p className="text-sm text-ink-muted">Chargement…</p>
            ) : demarches.length === 0 ? (
              <p className="text-sm text-ink-muted">Aucune démarche. Ajoutez-en une (CNI, passeport…).</p>
            ) : (
              <ul className="space-y-3">
                {demarches.map((d, idx) => (
                  <li key={d.id} className="rounded-r-lg border border-border bg-surface/50 p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-0.5 pt-0.5">
                        <button type="button" aria-label="Monter" disabled={idx === 0}
                          onClick={() => moveDemarche(idx, -1)}
                          className="text-ink-dim hover:text-ink disabled:opacity-30">
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Descendre" disabled={idx === demarches.length - 1}
                          onClick={() => moveDemarche(idx, 1)}
                          className="text-ink-dim hover:text-ink disabled:opacity-30">
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-ink">{d.titre}</span>
                          {d.aPdf && (
                            <Badge variant="success" size="sm">
                              <Paperclip className="mr-1 inline h-3 w-3" />{d.pdfNom ?? 'PDF'}
                            </Badge>
                          )}
                          {!d.affiche && <Badge variant="neutral" size="sm">Masquée</Badge>}
                        </div>
                        {d.texte && <p className="mt-1 text-sm text-ink-muted line-clamp-2">{d.texte}</p>}
                        {d.pieces.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {d.pieces.map((p, i) => (
                              <span key={i} className="rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">{p}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" aria-label={d.affiche ? 'Masquer' : 'Afficher'}
                          onClick={() => toggleDemarche(d)}
                          className="rounded-r-md p-1.5 text-ink-muted hover:bg-grc-tint/40 hover:text-grc-fg">
                          {d.affiche ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button type="button" aria-label="Modifier" onClick={() => setDemEdit(d)}
                          className="rounded-r-md p-1.5 text-ink-muted hover:bg-grc-tint/40 hover:text-grc-fg">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Supprimer" onClick={() => deleteDemarche(d)}
                          className="rounded-r-md p-1.5 text-ink-muted hover:bg-urgent-tint/40 hover:text-urgent">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionRepliable>
        </div>
      </main>

      {svcEdit && (
        <ServiceModal
          initial={svcEdit === 'new' ? null : svcEdit}
          onClose={() => setSvcEdit(null)}
          onSaved={() => { setSvcEdit(null); void reload(); }}
          onError={setError}
        />
      )}
      {demEdit && (
        <DemarcheModal
          initial={demEdit === 'new' ? null : demEdit}
          onClose={() => setDemEdit(null)}
          onSaved={() => { setDemEdit(null); void reload(); }}
          onError={setError}
        />
      )}

      {dialogElement}
    </GrcLayout>
  );
}

// ============================================================================
// Modale — Service horaire
// ============================================================================

function ServiceModal({
  initial, onClose, onSaved, onError,
}: {
  initial: ServiceHoraire | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [horaires, setHoraires] = useState(initial?.horaires ?? '');
  const [horairesEte, setHorairesEte] = useState(initial?.horairesEte ?? '');
  const [adresse, setAdresse] = useState(initial?.adresse ?? '');
  const [telephone, setTelephone] = useState(initial?.telephone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!nom.trim()) { onError('Le nom est requis'); return; }
    setBusy(true);
    const body = {
      nom: nom.trim(),
      horaires: horaires.trim() || null,
      horairesEte: horairesEte.trim() || null,
      adresse: adresse.trim() || null,
      telephone: telephone.trim() || null,
      email: email.trim() || null,
      note: note.trim() || null,
      affiche: initial?.affiche ?? true,
    };
    try {
      if (initial) await api.grc.poleInfo.updateService(initial.id, body);
      else await api.grc.poleInfo.createService(body);
      onSaved();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setBusy(false); }
  }

  return (
    <Dialog
      open onClose={onClose}
      title={initial ? 'Modifier le service' : 'Ajouter un service'}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Nom du service" htmlFor="svc-nom" required>
          <Input id="svc-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Accueil mairie" />
        </Field>
        <Field label="Horaires" htmlFor="svc-hor">
          <Input id="svc-hor" value={horaires} onChange={(e) => setHoraires(e.target.value)} placeholder="Lun–Ven 9h–12h / 14h–17h" />
        </Field>
        {/* Item 6.5 — remplacent les horaires normaux pendant une période
            saisonnière déclarée (onglet Périodes). Vide = pas de changement. */}
        <Field label="Horaires d'été (optionnels)" htmlFor="svc-hor-ete">
          <Input id="svc-hor-ete" value={horairesEte} onChange={(e) => setHorairesEte(e.target.value)} placeholder="Lun–Ven 8h–13h" />
        </Field>
        <Field label="Adresse" htmlFor="svc-adr">
          <Input id="svc-adr" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Téléphone" htmlFor="svc-tel">
            <Input id="svc-tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} />
          </Field>
          <Field label="Email" htmlFor="svc-mail">
            <Input id="svc-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>
        <Field label="Note (optionnelle)" htmlFor="svc-note">
          <textarea id="svc-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            className="w-full rounded-r-lg border border-border bg-surface/60 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
            placeholder="Ex. fermé le samedi en juillet-août" />
        </Field>
      </div>
    </Dialog>
  );
}

// ============================================================================
// Modale — Démarche (avec gestion des pièces + PDF)
// ============================================================================

function DemarcheModal({
  initial, onClose, onSaved, onError,
}: {
  initial: Demarche | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [titre, setTitre] = useState(initial?.titre ?? '');
  const [texte, setTexte] = useState(initial?.texte ?? '');
  const [pieces, setPieces] = useState<string[]>(initial?.pieces ?? []);
  const [pieceInput, setPieceInput] = useState('');
  // Retour de recette 02/08 : la mairie pose SES liens officiels. La détection
  // par mots-clés reste le filet quand cette liste est vide — donc les fiches
  // existantes ne perdent rien.
  const [liens, setLiens] = useState<LienDemarcheManuel[]>(initial?.liens ?? []);
  const [lienUrl, setLienUrl] = useState('');
  const [lienLabel, setLienLabel] = useState('');
  const [busy, setBusy] = useState(false);
  // PDF (uniquement en édition : il faut un id pour uploader)
  const [aPdf, setAPdf] = useState(initial?.aPdf ?? false);
  const [pdfNom, setPdfNom] = useState(initial?.pdfNom ?? null);

  function addPiece() {
    const v = pieceInput.trim();
    if (!v) return;
    setPieces((p) => [...p, v]);
    setPieceInput('');
  }
  function removePiece(i: number) {
    setPieces((p) => p.filter((_, j) => j !== i));
  }

  function addLien() {
    const url = lienUrl.trim();
    const label = lienLabel.trim() || 'Démarche en ligne';
    if (!url) return;
    if (!/^https:\/\/.+/.test(url)) {
      onError('Le lien doit commencer par https:// — une démarche officielle ne s\'ouvre jamais en clair.');
      return;
    }
    if (liens.length >= 4) { onError('4 liens maximum par fiche.'); return; }
    setLiens((l) => [...l, { url, label }]);
    setLienUrl(''); setLienLabel('');
  }
  function removeLien(i: number) {
    setLiens((l) => l.filter((_, j) => j !== i));
  }

  async function save() {
    if (!titre.trim()) { onError('Le titre est requis'); return; }
    setBusy(true);
    const body = { titre: titre.trim(), texte: texte.trim() || null, pieces, liens, affiche: initial?.affiche ?? true };
    try {
      if (initial) await api.grc.poleInfo.updateDemarche(initial.id, body);
      else await api.grc.poleInfo.createDemarche(body);
      onSaved();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setBusy(false); }
  }

  async function onPdfChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !initial) return;
    if (file.type !== 'application/pdf') { onError('Seuls les PDF sont acceptés'); return; }
    setBusy(true);
    try {
      const updated = await api.grc.poleInfo.uploadDemarchePdf(initial.id, file);
      setAPdf(updated.aPdf); setPdfNom(updated.pdfNom);
    } catch (err) {
      onError(err instanceof ApiException ? err.message : 'Upload impossible');
    } finally { setBusy(false); }
  }
  async function removePdf() {
    if (!initial) return;
    setBusy(true);
    try {
      const updated = await api.grc.poleInfo.removeDemarchePdf(initial.id);
      setAPdf(updated.aPdf); setPdfNom(updated.pdfNom);
    } catch (err) {
      onError(err instanceof ApiException ? err.message : 'Suppression impossible');
    } finally { setBusy(false); }
  }

  return (
    <Dialog
      open onClose={onClose}
      title={initial ? 'Modifier la démarche' : 'Ajouter une démarche'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Titre" htmlFor="dem-titre" required>
          <Input id="dem-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Carte nationale d'identité (CNI)" />
        </Field>
        <Field label="Texte explicatif" htmlFor="dem-texte">
          <textarea id="dem-texte" value={texte} onChange={(e) => setTexte(e.target.value)} rows={4}
            className="w-full rounded-r-lg border border-border bg-surface/60 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
            placeholder="Comment effectuer la démarche, où, délais…" />
        </Field>

        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">Pièces à fournir</label>
          <div className="flex gap-2">
            <Input value={pieceInput} onChange={(e) => setPieceInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPiece(); } }}
              placeholder="Justificatif de domicile…" />
            <Button variant="secondary" onClick={addPiece}>Ajouter</Button>
          </div>
          {pieces.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {pieces.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2.5 py-1 text-xs text-ink-muted">
                  {p}
                  <button type="button" aria-label="Retirer" onClick={() => removePiece(i)} className="text-ink-dim hover:text-urgent">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Liens officiels (recette 02/08) — la mairie pose les siens ; s'il n'y
            en a aucun, la détection automatique par le titre prend le relais. */}
        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">Liens officiels</label>
          <p className="mb-1.5 text-xs text-ink-dim">
            Affichés sous la fiche côté administré (4 max, https uniquement).
            Sans lien saisi ici, un lien est proposé automatiquement d'après le
            titre quand il est reconnu (CNI, passeport, timbre fiscal, amende…).
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Input value={lienUrl} onChange={(e) => setLienUrl(e.target.value)}
              placeholder="https://timbres.impots.gouv.fr/" aria-label="Adresse du lien" />
            <Input value={lienLabel} onChange={(e) => setLienLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLien(); } }}
              placeholder="Acheter un timbre fiscal" aria-label="Libellé du lien" />
            <Button variant="secondary" onClick={addLien}>Ajouter</Button>
          </div>
          {liens.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {liens.map((l, i) => (
                <div key={i} className="flex items-center justify-between gap-2 rounded-r-lg border border-border bg-bg-2 px-2.5 py-1.5 text-xs">
                  <span className="min-w-0 truncate text-ink-muted">
                    <span className="font-medium text-ink">{l.label}</span> — {l.url}
                  </span>
                  <button type="button" aria-label="Retirer ce lien" onClick={() => removeLien(i)}
                          className="flex-shrink-0 text-ink-dim hover:text-urgent">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PDF : uniquement en édition */}
        <div className="rounded-r-lg border border-border bg-bg/40 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-ink">
            <Paperclip className="h-4 w-4 text-grc" /> Document PDF (optionnel)
          </div>
          {!initial ? (
            <p className="mt-1.5 text-xs text-ink-dim">Enregistrez d'abord la démarche, puis rouvrez-la pour joindre un PDF.</p>
          ) : aPdf ? (
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-sm text-ink-muted truncate">{pdfNom ?? 'Document PDF'}</span>
              <Button variant="ghost" size="sm" onClick={removePdf} disabled={busy}>
                <Trash2 className="h-4 w-4" /> Retirer
              </Button>
            </div>
          ) : (
            <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-r-pill border border-border bg-surface px-3 py-1.5 text-sm text-ink hover:border-border-strong">
              <Plus className="h-4 w-4" /> Joindre un PDF
              <input type="file" accept="application/pdf" className="hidden" onChange={onPdfChange} disabled={busy} />
            </label>
          )}
        </div>
      </div>
    </Dialog>
  );
}
